import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import api from '../lib/api';
import { getNotificationAction, buildActionUrl, dispatchRefresh } from '../lib/notificationActions';
import { LogOut, User, Briefcase, Heart, Shield, Bell, X, Check, Settings, HelpCircle } from 'lucide-react';
import { Logo } from './Logo';
import Footer from './Footer';

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotifPanel, setShowNotifPanel] = useState(false);
    const panelRef = useRef(null);
    const knownNotifIdsRef = useRef(new Set());

    const fetchNotifications = useCallback(async () => {
        if (!user) return;
        try {
            const [notifRes, countRes] = await Promise.all([
                api.get('/notifications'),
                api.get('/notifications/unread-count'),
            ]);
            const list = notifRes.data.notifications || [];
            // Detect newly-arrived notifications since last poll and trigger a
            // silent background refresh of the relevant dashboard data.
            if (knownNotifIdsRef.current.size > 0) {
                const freshKeys = new Set();
                for (const n of list) {
                    if (!knownNotifIdsRef.current.has(n.id)) {
                        const action = getNotificationAction(user.role, n.type);
                        if (action?.refreshKey) freshKeys.add(action.refreshKey);
                    }
                }
                if (freshKeys.size > 0) dispatchRefresh(Array.from(freshKeys));
            }
            knownNotifIdsRef.current = new Set(list.map(n => n.id));
            setNotifications(list);
            setUnreadCount(countRes.data.count);
        } catch (err) {
            console.error('Notification poll failed:', err);
        }
    }, [user]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 15000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    // Register push subscription
    useEffect(() => {
        if (!user) return;
        registerPushSubscription();
    }, [user]);

    const registerPushSubscription = async () => {
        if (process.env.NODE_ENV !== 'production') return;
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
        const reg = await navigator.serviceWorker.ready;
        const existingSub = await reg.pushManager.getSubscription();
        if (existingSub) {
            await api.post('/notifications/subscribe', {
                endpoint: existingSub.endpoint,
                keys: {
                    p256dh: btoa(String.fromCharCode(...new Uint8Array(existingSub.getKey('p256dh')))),
                    auth: btoa(String.fromCharCode(...new Uint8Array(existingSub.getKey('auth'))))
                }
            });
            return;
        }
        const { data } = await api.get('/notifications/vapid-key');
        if (!data.public_key) return;
        const applicationServerKey = urlBase64ToUint8Array(data.public_key);
        const subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey
        });
        await api.post('/notifications/subscribe', {
            endpoint: subscription.endpoint,
            keys: {
                p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
                auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
            }
        });
    };

    const markAllRead = async () => {
        try {
            await api.put('/notifications/read-all');
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch (err) {
            console.error('markAllRead failed:', err);
        }
    };

    const markRead = async (id) => {
        try {
            await api.put(`/notifications/${id}/read`);
            // Local state first (snappy UI), then re-sync unread-count from
            // the server to avoid a race where an in-flight 15s poll could
            // otherwise overwrite the optimistic decrement with a stale count.
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
            try {
                const { data } = await api.get('/notifications/unread-count');
                setUnreadCount(data.count);
            } catch (_) { /* keep optimistic value */ }
        } catch (err) {
            console.error('markRead failed:', err);
        }
    };

    // Click a notification → mark read, navigate to its dashboard section,
    // and trigger a silent refetch on that section so the new data is visible
    // immediately. Closes the panel.
    //
    // Re-click handling: we always append a unique `_t` cache-buster to the
    // URL so even clicking the same notification twice produces a *different*
    // URL on the second click. Without this, navigate() with an unchanged URL
    // is a no-op and the destination's [searchParams] effect won't re-run,
    // so the tab won't switch / the section won't scroll. Read notifications
    // therefore still navigate on every click.
    const handleNotificationClick = async (n) => {
        setShowNotifPanel(false);
        if (!n.read) {
            // Optimistic — don't block navigation on the API
            markRead(n.id);
        }
        if (!user) return;
        const action = getNotificationAction(user.role, n.type);
        if (!action) return;
        const base = buildActionUrl(action) || action.path;
        const sep = base.includes('?') ? '&' : '?';
        const url = `${base}${sep}_t=${Date.now()}`;
        navigate(url);
        // Always dispatch a refresh so the destination updates even if it was
        // already mounted on the route (no remount → no fetch).
        if (action.refreshKey) {
            // small delay so the destination listener is registered after route change
            setTimeout(() => dispatchRefresh([action.refreshKey]), 50);
        }
        if (action.scrollId) {
            setTimeout(() => {
                const el = document.getElementById(action.scrollId);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
        }
    };

    const timeAgo = (dateStr) => {
        const diff = Date.now() - new Date(dateStr).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'Just now';
        if (mins < 60) return `${mins} minutes ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours} hours ago`;
        const days = Math.floor(hours / 24);
        if (days === 1) return 'Yesterday';
        if (days < 7) return `${days} days ago`;
        return new Date(dateStr).toLocaleDateString();
    };

    // Close panel on outside click
    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setShowNotifPanel(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const roleLabel = {
        worker: 'Worker',
        employer: 'Employer',
        medical: 'Medical Partner',
        admin: 'Admin'
    };

    const roleIcon = {
        worker: <User className="w-4 h-4" />,
        employer: <Briefcase className="w-4 h-4" />,
        medical: <Heart className="w-4 h-4" />,
        admin: <Shield className="w-4 h-4" />
    };

    const typeIcon = {
        offer: <Briefcase className="w-4 h-4 text-blue-600" />,
        offer_response: <Check className="w-4 h-4 text-green-600" />,
        visa_update: <Shield className="w-4 h-4 text-purple-600" />,
        medical: <Heart className="w-4 h-4 text-red-600" />,
        info: <Bell className="w-4 h-4 text-zinc-500" />,
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-zinc-200" data-testid="app-header">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14">
                        <button
                            onClick={() => navigate(user ? `/${user.role}` : '/')}
                            className="text-zinc-900 hover:opacity-70 transition-opacity"
                            data-testid="logo-btn"
                            aria-label="GulfBridge home"
                        >
                            <Logo size="md" data-testid="nav-logo" />
                        </button>

                        {user && (
                            <div className="flex items-center gap-2">
                                {/* Notification Bell */}
                                <div className="relative" ref={panelRef}>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="rounded-none h-9 w-9 relative"
                                        onClick={() => setShowNotifPanel(prev => !prev)}
                                        data-testid="notification-bell-btn"
                                    >
                                        <Bell className="w-4 h-4" />
                                        {unreadCount > 0 && (
                                            <span className={`absolute -top-0.5 -right-0.5 h-4 ${unreadCount > 9 ? 'min-w-[20px] px-1' : 'w-4'} bg-red-600 text-white text-[10px] font-bold flex items-center justify-center pulse-dot rounded-sm`} data-testid="unread-badge">
                                                {unreadCount > 99 ? '99+' : unreadCount}
                                            </span>
                                        )}
                                    </Button>

                                    {/* Notification Panel — desktop: dropdown anchored to bell. Mobile: full-width bottom sheet. */}
                                    {showNotifPanel && (
                                        <>
                                            {/* Mobile backdrop — tap outside to close */}
                                            <button
                                                type="button"
                                                aria-label="Close notifications"
                                                className="sm:hidden fixed inset-0 bg-black/40 z-[59]"
                                                onClick={() => setShowNotifPanel(false)}
                                                data-testid="notification-backdrop"
                                            />
                                            <div
                                                className="
                                                    fixed left-0 right-0 top-14 z-[60]
                                                    sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:inset-x-auto
                                                    w-full sm:w-96
                                                    bg-white border-2 border-zinc-900 shadow-2xl flex flex-col
                                                    max-h-[80vh]
                                                "
                                                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                                                data-testid="notification-panel"
                                            >
                                                {/* Mobile-only drag handle */}
                                                <div className="sm:hidden flex justify-center pt-2">
                                                    <div className="w-10 h-1 rounded-full bg-zinc-300" />
                                                </div>
                                                <div className="flex items-center justify-between p-3 border-b border-zinc-200">
                                                    <span className="text-sm font-bold tracking-wide">Notifications</span>
                                                    <div className="flex items-center gap-2">
                                                        {unreadCount > 0 && (
                                                            <button onClick={markAllRead} className="text-xs text-zinc-500 hover:text-zinc-900 font-semibold" data-testid="mark-all-read-btn">
                                                                Mark all read
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowNotifPanel(false)}
                                                            className="sm:hidden text-zinc-400 hover:text-zinc-900 p-1"
                                                            aria-label="Close"
                                                            data-testid="notification-close-btn"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="overflow-y-auto flex-1">
                                                    {notifications.length === 0 ? (
                                                        <div className="p-6 text-center text-sm text-zinc-400" data-testid="no-notifications">
                                                            No notifications yet
                                                        </div>
                                                    ) : (
                                                        notifications.slice(0, 20).map(n => (
                                                            <button
                                                                type="button"
                                                                key={n.id}
                                                                className={`w-full text-left flex items-start gap-3 p-3 border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors ${!n.read ? 'bg-zinc-50' : ''}`}
                                                                onClick={() => handleNotificationClick(n)}
                                                                data-testid={`notification-${n.id}`}
                                                            >
                                                                <div className="flex-shrink-0 mt-0.5">
                                                                    {typeIcon[n.type] || typeIcon.info}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className={`text-sm ${!n.read ? 'font-semibold' : ''}`}>{n.title}</p>
                                                                    <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{n.message}</p>
                                                                    <p className="text-[10px] text-zinc-400 mt-1">{timeAgo(n.created_at)}</p>
                                                                </div>
                                                                {!n.read && <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1.5" />}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="hidden sm:flex items-center gap-2 text-sm text-zinc-500">
                                    {roleIcon[user.role]}
                                    <span>{roleLabel[user.role]}</span>
                                </div>
                                <div className="hidden sm:block h-4 w-px bg-zinc-200" />
                                <span className="hidden sm:block text-sm font-medium text-zinc-900">{user.name || user.company_name}</span>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => navigate('/faq')}
                                    className="rounded-none h-9 w-9"
                                    title="Help / FAQ"
                                    data-testid="faq-btn"
                                >
                                    <HelpCircle className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => navigate('/settings')}
                                    className="rounded-none h-9 w-9"
                                    data-testid="settings-btn"
                                >
                                    <Settings className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={handleLogout}
                                    className="rounded-none h-9 w-9"
                                    data-testid="logout-btn"
                                >
                                    <LogOut className="w-4 h-4" />
                                </Button>
                            </div>
                        )}

                        {!user && location.pathname !== '/auth' && (
                            <Button
                                variant="outline"
                                className="rounded-none h-10 border-zinc-900"
                                onClick={() => navigate('/auth')}
                                data-testid="signin-btn"
                            >
                                Sign In
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24 sm:pb-6 w-full flex-1">
                {children}
            </main>
            <Footer />
        </div>
    );
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}
